import React, { useState } from 'react';
import { BanknoteIcon, DownloadIcon, InfoIcon } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { Card, KeyValue, Metric, SectionHeading } from '../../components/ui/Panels';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { useLocale } from '../../contexts/LocaleContext';
import { useSession } from '../../contexts/SessionContext';
import { chefEarnings, chefPayouts, chefTopMenus } from '../../data/stats';
import { currency, dateWithYear } from '../../utils/format';
import { SERVICE_FEE_RATE } from '../../utils/pricing';
export function Earnings() {
  const {
    locale,
    currencyCode
  } = useLocale();
  const {
    chef
  } = useSession();
  const [hovered, setHovered] = useState<string | null>(null);
  const peak = Math.max(...chefEarnings.map(p => p.amount));
  const total = chefEarnings.reduce((sum, p) => sum + p.amount, 0);
  const bookings = chefEarnings.reduce((sum, p) => sum + p.bookings, 0);
  const month = chefEarnings[chefEarnings.length - 1];
  const active = chefEarnings.find(p => p.month === hovered) ?? month;
  const grossFromNet = Math.round(month.amount / (1 - SERVICE_FEE_RATE));
  return <AppShell title="Earnings" subtitle="After the 10% service fee, before your own costs" data-id="element-1082" data-mp="erav2bnpsblu">
      <Card className="grid gap-6 p-5 sm:grid-cols-3" data-id="element-1083" data-mp="irutrjnxrna3">
        <Metric label="Take-home, six months" value={currency(total, locale, currencyCode)} detail={`${bookings} bookings`} tone="primary" data-id="element-1084" data-mp="4twkgsoh2h1g" />
        <Metric label="Average per booking" value={currency(Math.round(total / bookings), locale, currencyCode)} detail="net of the service fee" data-id="element-1085" data-mp="3rycfx84iqi97" />
        <Metric label="Minimum order" value={currency(chef.minimumSpend, locale, currencyCode)} detail="quotes below this are blocked" data-id="element-1086" data-mp="3w1r4in6iw6y9" />
      </Card>

      <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-1087" data-mp="1u6gqm5boynw1">
        <div className="space-y-8" data-id="element-1088" data-mp="3372s5ujumxn8">
          <section aria-labelledby="chart-heading" data-id="element-1089" data-mp="33359fa16o83v">
            <SectionHeading level={2} title="Month by month" hint="Hover a bar for the detail." data-id="element-1090" data-mp="2y5n742ho8r4u" />
            <Card className="mt-4 p-5" data-id="element-1091" data-mp="2u5mg7iddyngw">
              <div className="flex items-baseline justify-between gap-3" data-id="element-1092" data-mp="2u6o3qmbl92fi">
                <div data-id="element-1093" data-mp="2p7kyp1vlyw7j">
                  <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1094" data-mp="2p835gsfwcstp">{active.month}</p>
                  <p className="tnum font-display text-2xl font-semibold text-ink" data-id="element-1095" data-mp="3hunu3l4zmqtk">
                    {currency(active.amount, locale, currencyCode)}
                  </p>
                </div>
                <p className="tnum text-[13px] text-ink-2" data-id="element-1096" data-mp="384m58r4t94dl">{active.bookings} bookings</p>
              </div>

              <div className="mt-5 flex h-40 items-end gap-2.5" role="img" aria-label="Monthly take-home over six months" data-id="element-1097" data-mp="9ecb3rxei4w5">
                {chefEarnings.map(point => <div key={point.month} className="flex flex-1 flex-col items-center gap-2" data-id="element-1098" data-mp="4m18ghba04zw">
                    <button type="button" onMouseEnter={() => setHovered(point.month)} onMouseLeave={() => setHovered(null)} onFocus={() => setHovered(point.month)} onBlur={() => setHovered(null)} aria-label={`${point.month}: ${currency(point.amount, locale, currencyCode)}`} className={`w-full rounded-t transition-colors duration-150 ease-out ${active.month === point.month ? 'bg-primary' : 'bg-primary/25 hover:bg-primary/45'}`} style={{
                  height: `${point.amount / peak * 100}%`
                }} data-id="element-1099" data-mp="c14xiuag1v9p" />
                    <span className="text-[11px] text-ink-3" data-id="element-1100" data-mp="1dow5e3tkji6i">{point.month}</span>
                  </div>)}
              </div>
            </Card>
          </section>

          <section aria-labelledby="menus-heading" data-id="element-1101" data-mp="150qium4x8uaf">
            <SectionHeading level={2} title="What actually earns" hint="Lifetime, by menu." data-id="element-1102" data-mp="14zfdwizqpghq" />
            <Card className="mt-4 divide-y divide-hairline" data-id="element-1103" data-mp="10y3jm05x8737">
              {chefTopMenus.map(menu => <div key={menu.name} className="flex flex-wrap items-center gap-3 px-5 py-4" data-id="element-1104" data-mp="1ep8hawpqapaw">
                  <span className="min-w-0 flex-1" data-id="element-1105" data-mp="19srp9rferqsj">
                    <span className="block text-sm font-medium text-ink" data-id="element-1106" data-mp="m1w0722c79oz">{menu.name}</span>
                    <span className="block text-[12px] text-ink-3" data-id="element-1107" data-mp="i0ain3euy78g">{menu.bookings} bookings</span>
                  </span>
                  <span className="tnum shrink-0 font-display text-base font-semibold text-ink" data-id="element-1108" data-mp="w0lb2avtftvq">
                    {currency(menu.revenue, locale, currencyCode)}
                  </span>
                  <span className="h-1.5 w-full overflow-hidden rounded-full bg-oat" data-id="element-1109" data-mp="27eyu92fmjl5f">
                    <span className="block h-full rounded-full bg-primary" style={{
                  width: `${menu.revenue / chefTopMenus[0].revenue * 100}%`
                }} data-id="element-1110" data-mp="22g4ek9cyecpj" />
                  </span>
                </div>)}
            </Card>
          </section>

          <section aria-labelledby="payout-heading" data-id="element-1111" data-mp="3bdcckn0po77p">
            <SectionHeading level={2} title="Payouts" hint="Twice a month, two working days after each period closes." action={<Button type="button" variant="outline" size="sm" data-id="element-1113" data-mp="7xnbduvjxkfa">
                  <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1114" data-mp="bygzopcwp18p" />
                  Statements
                </Button>} data-id="element-1112" data-mp="3benhwr9t1n0x" />
            <Card className="mt-4 divide-y divide-hairline" data-id="element-1115" data-mp="3u9ubbodz97dg">
              {chefPayouts.map(payout => <div key={payout.id} className="flex flex-wrap items-center gap-3 px-5 py-4" data-id="element-1116" data-mp="33aqrhica31y">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-oat text-ink-2" aria-hidden="true" data-id="element-1117" data-mp="qt537xyqxplo">
                    <BanknoteIcon className="h-4 w-4" data-id="element-1118" data-mp="15hn7tanyqexy" />
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-1119" data-mp="15fkkiye72zwx">
                    <span className="block text-sm font-medium text-ink" data-id="element-1120" data-mp="10k590d3ntg3o">{payout.period}</span>
                    <span className="block text-[12px] text-ink-3" data-id="element-1121" data-mp="vippiph4rhne">
                      {payout.status === 'paid' ? 'Paid' : 'Arriving'} {dateWithYear(payout.arriving, locale)}
                    </span>
                  </span>
                  <Badge tone={payout.status === 'paid' ? 'success' : 'accent'} data-id="element-1122" data-mp="mkdr41a0vfke">{payout.status}</Badge>
                  <span className="tnum shrink-0 font-display text-base font-semibold text-ink" data-id="element-1123" data-mp="hq9f33lwghcg">
                    {currency(payout.amount, locale, currencyCode)}
                  </span>
                </div>)}
            </Card>
          </section>
        </div>

        <aside className="space-y-4" data-id="element-1124" data-mp="2uk1ntus5cjc">
          <Card className="p-5" data-id="element-1125" data-mp="3uyuo0culq3c5">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1126" data-mp="3uymaqjukgw0f">August, in full</h2>
            <dl className="mt-3" data-id="element-1127" data-mp="2ag200z8doqqx">
              <KeyValue label="Guests paid" value={currency(grossFromNet, locale, currencyCode)} data-id="element-1128" data-mp="2aiwjoojki7ja" />
              <KeyValue label="Service fee (10%)" value={`− ${currency(grossFromNet - month.amount, locale, currencyCode)}`} data-id="element-1129" data-mp="2eki4cw2cfq41" />
              <KeyValue label="Your take-home" value={<span className="text-primary" data-id="element-1131" data-mp="2oeifpfo7xrl7">{currency(month.amount, locale, currencyCode)}</span>} data-id="element-1130" data-mp="2ogcqi15s03bk" />
            </dl>
            <p className="mt-3 text-[12px] leading-relaxed text-ink-3" data-id="element-1132" data-mp="1vrnx6oayp2ad">
              VAT is collected from the guest and remitted separately — it never passes through your balance.
            </p>
          </Card>

          <Card className="p-5" data-id="element-1133" data-mp="25i0gkd71ipex">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1134" data-mp="25j1tkn7m9lza">Bank account</h2>
            <p className="mt-2 text-sm font-medium text-ink" data-id="element-1135" data-mp="1808tos7g8exj">Kasikornbank ···· 4182</p>
            <p className="text-[12px] text-ink-3" data-id="element-1136" data-mp="1czv30qk5pb12">{chef.name} · Bangkok</p>
            <Button type="button" variant="ghost" size="sm" className="mt-3" data-id="element-1137" data-mp="6f566lutsls0">
              Change payout account
            </Button>
          </Card>

          <Card className="p-5" data-id="element-1138" data-mp="1hde9ru6kznt">
            <div className="flex items-start gap-2.5" data-id="element-1139" data-mp="ff2298jjomx3">
              <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" data-id="element-1140" data-mp="fcfjecr7ekzk" />
              <p className="text-[13px] leading-relaxed text-ink-2" data-id="element-1141" data-mp="ahtkh92b6kwd">
                At-cost groceries are reimbursed in full on top of these figures once you upload the receipts to a booking.
              </p>
            </div>
          </Card>
        </aside>
      </div>
    </AppShell>;
}